/**
 * Activation zones — data/editor compatibility only for ordinary scenery.
 *
 * Runtime gameplay is direct-click-only: walking near or arriving at a zone
 * never focuses scenery. Zones may later power explicit travel portals.
 */

import type {
  ActivationZone,
  InteractiveObject,
  SpawnPoint,
} from '@/world-prototype/types';

/** Authored activation zone (offsets from the object anchor / scatter center). */
export interface ActivationZoneDef {
  shape: 'circle' | 'rect';
  /** Center offset from the object ground/center anchor. */
  x: number;
  y: number;
  /** Circle radius in world-width units (aspect-corrected on Y). */
  radius?: number;
  /** Rect full width/height in world fractions. */
  width?: number;
  height?: number;
  /** When false, zone is disabled in data (still ignored by ordinary gameplay). */
  enabled?: boolean;
}

export function isActivationZoneEnabled(
  zone: ActivationZone | ActivationZoneDef | null | undefined,
): boolean {
  return zone != null && zone.enabled !== false;
}

/**
 * True when `point` lies inside the zone.
 * Circles use world-width units for radius (same convention as hitRadius).
 * Editor / debug only for ordinary scenery.
 */
export function pointInActivationZone(
  point: SpawnPoint,
  zone: ActivationZone,
  worldAspect: number,
): boolean {
  if (!isActivationZoneEnabled(zone)) return false;

  if (zone.shape === 'circle') {
    const radius = zone.radius ?? 0;
    if (radius <= 0) return false;
    const aspect = worldAspect > 0 ? worldAspect : 1;
    const dx = point.x - zone.x;
    const dy = (point.y - zone.y) / aspect;
    return dx * dx + dy * dy <= radius * radius;
  }

  const width = zone.width ?? 0;
  const height = zone.height ?? 0;
  if (width <= 0 || height <= 0) return false;
  const halfW = width / 2;
  const halfH = height / 2;
  return (
    point.x >= zone.x - halfW &&
    point.x <= zone.x + halfW &&
    point.y >= zone.y - halfH &&
    point.y <= zone.y + halfH
  );
}

/**
 * Legacy helper: nearest object whose activation zone contains `destination`.
 * Ordinary gameplay must not call this for scenery focus.
 */
export function resolveActivationAtDestination(
  destination: SpawnPoint,
  objects: readonly InteractiveObject[],
  worldAspect: number,
): InteractiveObject | null {
  let best: InteractiveObject | null = null;
  let bestDist = Infinity;

  for (const obj of objects) {
    const zone = obj.activationZone;
    if (!zone || !pointInActivationZone(destination, zone, worldAspect)) {
      continue;
    }
    const dist = Math.hypot(destination.x - zone.x, destination.y - zone.y);
    if (dist < bestDist) {
      bestDist = dist;
      best = obj;
    }
  }

  return best;
}

/**
 * Focus policy after arrival.
 *
 * Direct object clicks return that object. Ground walks never focus scenery
 * (activation zones are ignored for ordinary gameplay).
 */
export function focusTargetOnArrive(
  intent: { kind: 'ground' } | { kind: 'object'; objectId: string },
  _destination: SpawnPoint,
  objects: readonly InteractiveObject[],
  _worldAspect: number,
): InteractiveObject | null {
  if (intent.kind === 'object') {
    return objects.find((o) => o.id === intent.objectId) ?? null;
  }
  return null;
}

/** Place an authored (local) zone into world space at an anchor. */
export function placeActivationZone(
  def: ActivationZoneDef | undefined,
  anchor: SpawnPoint,
  localCenter: SpawnPoint,
): ActivationZone | undefined {
  if (!def) return undefined;
  return {
    shape: def.shape,
    x: anchor.x + localCenter.x,
    y: anchor.y + localCenter.y,
    radius: def.radius,
    width: def.width,
    height: def.height,
    enabled: def.enabled,
  };
}
